import React, { useEffect } from 'react';
import { Container, Box, Drawer, CssBaseline, Badge, Chip, Typography, useMediaQuery, Modal, Backdrop, Fade, Divider, Button } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { alpha, keyframes } from '@mui/material/styles';
import theme from './theme';
import Header from './Header';
import BlackHeader from './BlackHeader';
/* import Cart from './Cart'; */
import { CartProvider, useCart } from './CartContext';
import Home from './Home';
import { getPriceInCurrency, getCurrencyDecimals } from './currencyHelpers';
import { useCurrency } from './CurrencyContext';
/* import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'; */
/* import LocalOfferIcon from '@mui/icons-material/LocalOffer'; */
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PrePaymentStatusPage from './PrePaymentOrder/PrePaymentStatusPage';
import Categories from './Categories';
import Products from './Products';
import HomeCustomProductsSection from './HomeCustomProductsSection';
import AIChat from './AIChat';
import Footer from './Footer';
import BranchFooter from './BranchFooter';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Register from './Register';
import Login from './Login';
import { CheckoutPage } from './Checkout';
import UserProfile from './UserProfile';
import OrderStatusPage from './OrderStatusPage';
import TrackOrderPage from './TrackOrderPage';
import OrderHistoryPage from './OrderHistoryPage';
import OffersPage from './OffersPage';
import Offers from './Offers';
import Favorites from './Favorites';
import Locations from './Locations';
import AddEditLocation from './AddEditLocation';
import AboutUs from './AboutUs';
import PoliciesTerms from './PoliciesTerms';
import Support from './Support';
import GroupedProducts from './GroupedProducts';
import HomeOffersPreview from './HomeOffersPreview';
import HomeCustomProductsPreview from './HomeCustomProductsPreview';
import CustomProducts from './CustomProducts';
import SearchBarEnhanced from './SearchBarEnhanced';
import WhatsAppIcon from './whatsapp/WhatsAppIcon';
import { useTranslation, Translation } from 'react-i18next';
import AdminApp from './AdminPortal/App';
import BranchSelectionPage from './BranchSelectionPage';
import JoinStoresYe from './JoinStoresYe';
import { Navigate, useLocation } from 'react-router-dom';
import { fetchBranches, fetchBranchCities } from './branchesApi';
import RentalsPage from './RentalsPage';
import RentalDetailPage from './RentalDetailPage';
import MyRentalsPage from './MyRentalsPage';
import HomeRentalsPreview from './HomeRentalsPreview';
import BranchConnectedSection from './BranchConnectedSection';

// ✨ Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const shine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// ✅ NEW: Branch Link Handler Component
function BranchLinkHandler() {
  const { branchId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSelectedBranch } = useCart();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  useEffect(() => {
    const handleBranchSelection = async () => {
      try {
        const id = branchId || searchParams.get('branchId');
        
        if (!id) {
          console.log('⚠️ No branch ID provided');
          navigate('/select-branch');
          return;
        }

        console.log('🔗 Branch ID from URL:', id);

        const branches = await fetchBranches();
        
        const branch = branches.find(
          b => String(b.id) === String(id) || String(b.Id) === String(id)
        );

        if (branch) {
          console.log('✅ Branch found:', branch.name);
          
          setSelectedBranch(branch);
          
          const event = new CustomEvent('showSnackbar', {
            detail: {
              message: t('branch.selected', `Selected: ${branch.name || branch.Name}`),
              severity: 'success'
            }
          });
          window.dispatchEvent(event);
          
          setTimeout(() => {
            navigate('/');
          }, 500);
        } else {
          console.log('❌ Branch not found with ID:', id);
          setError(t('branch.notFound', 'Branch not found'));
          
          setTimeout(() => {
            navigate('/select-branch');
          }, 2000);
        }
      } catch (err) {
        console.error('❌ Error handling branch link:', err);
        setError(t('branch.loadError', 'Failed to load branch'));
        
        setTimeout(() => {
          navigate('/select-branch');
        }, 2000);
      } finally {
        setLoading(false);
      }
    };

    handleBranchSelection();
  }, [branchId, searchParams, navigate, setSelectedBranch, t]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          gap: 3
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            border: '6px solid',
            borderColor: alpha(theme.palette.primary.main, 0.2),
            borderTopColor: theme.palette.primary.main,
            borderRadius: '50%',
            animation: `${spin} 1s linear infinite`
          }}
        />
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {t('branch.loading', 'Loading branch...')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          gap: 2,
          px: 2
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main', textAlign: 'center' }}>
          {error}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          {t('branch.redirecting', 'Redirecting to branch selection...')}
        </Typography>
      </Box>
    );
  }

  return null;
}

// ✅ NEW: Product Link Handler
function ProductLinkHandler() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  useEffect(() => {
    const handleProductLink = async () => {
      try {
        if (!productId) {
          navigate('/');
          return;
        }

        console.log('🔗 Product Link - Product ID:', productId);
        setLoading(true);

        const productResponse = await fetch(`https://elorayem.com/stores/Product/${productId}`);
        if (!productResponse.ok) throw new Error('Product not found');
        
        const product = await productResponse.json();
        const branchId = product.BranchId || product.branchId;
        
        console.log('🏢 Product belongs to branch:', branchId);

        if (!branchId) throw new Error('Product has no branch');

        const branches = await fetchBranches();
        const branch = branches.find(b => String(b.id) === String(branchId) || String(b.Id) === String(branchId));

        if (!branch) throw new Error('Branch not found');

        console.log('✅ Found branch:', branch.name);

        localStorage.setItem('pendingProductId', productId);

        navigate('/select-branch', {
          state: {
            selectedBranch: branch,
            fromProductLink: true
          },
          replace: true
        });

      } catch (err) {
        console.error('❌ Error:', err);
        setError(err.message);
        setTimeout(() => navigate('/'), 2000);
      } finally {
        setLoading(false);
      }
    };

    handleProductLink();
  }, [productId, navigate, t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <Box sx={{ width: 60, height: 60, border: '4px solid', borderColor: alpha(theme.palette.primary.main, 0.2), borderTopColor: theme.palette.primary.main, borderRadius: '50%', animation: `${spin} 1s linear infinite` }} />
        <Typography variant="h6">{t('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2, px: 2 }}>
        <Typography variant="h6" color="error">❌ {error}</Typography>
        <Typography variant="body2" color="text.secondary">{t('common.redirecting', 'Redirecting...')}</Typography>
      </Box>
    );
  }

  return null;
}

const RequireBranch = ({ children }) => {
  const { selectedBranch } = useCart();
  const location = useLocation();
  
  const publicRoutes = ['/admin', '/login', '/register', '/select-branch','/product', '/support', '/app', '/branch', '/join'];
  const isPublic = publicRoutes.some(path => location.pathname.startsWith(path));

  if (isPublic) {
    return children;
  }

  if (!selectedBranch) {
    return <Navigate to="/select-branch" replace />;
  }

  return children;
};

// ✅ NEW: City Mismatch Guard - checks if branch delivers to user's city
function CityMismatchGuard({ children }) {
  const { selectedBranch } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const themeObj = useTheme();
  
  const [cityModalOpen, setCityModalOpen] = React.useState(false);
  const [branchCities, setBranchCities] = React.useState([]);
  const cityCheckDoneRef = React.useRef(false);
  const lastBranchIdRef = React.useRef(null);

  React.useEffect(() => {
    if (!selectedBranch) return;

    const branchId = selectedBranch.id || selectedBranch.Id;
    
    // Reset check if branch changed
    if (lastBranchIdRef.current !== branchId) {
      cityCheckDoneRef.current = false;
      lastBranchIdRef.current = branchId;
    }

    if (cityCheckDoneRef.current) return;

    const checkCityMismatch = async () => {
      try {
        // Get user data
        let userCity = '';
        try {
          const userData = JSON.parse(localStorage.getItem('user'));
          userCity = (userData?.city || userData?.City || userData?.cityName || userData?.CityName || '').trim().toLowerCase();
        } catch {}

        // If user has no city, skip check
        if (!userCity) { cityCheckDoneRef.current = true; return; }

        // Fetch branch delivery cities
        const cities = await fetchBranchCities(branchId);

        // If branch has no cities assigned, skip check (no restrictions)
        if (!cities || cities.length === 0) { cityCheckDoneRef.current = true; return; }

        // Check if user's city matches any branch delivery city
        const delivers = cities.some(c => (c.Name || c.name || '').trim().toLowerCase() === userCity);

        if (!delivers) {
          setBranchCities(cities);
          setCityModalOpen(true);
        }

        cityCheckDoneRef.current = true;
      } catch {
        cityCheckDoneRef.current = true;
      }
    };

    checkCityMismatch();
  }, [selectedBranch]);

  return (
    <>
      {children}

      {/* ✅ CITY MISMATCH MODAL */}
      <Modal
        open={cityModalOpen}
        onClose={() => {}} // Prevent closing by clicking backdrop
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 400,
          sx: { backdropFilter: 'blur(6px)', backgroundColor: alpha(themeObj.palette.common.black, 0.5) }
        }}
      >
        <Fade in={cityModalOpen}>
          <Box sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 440, md: 500 },
            bgcolor: 'background.paper',
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: 24,
            p: { xs: 3, sm: 4, md: 5 },
            outline: 'none',
            textAlign: 'center'
          }}>
            {/* Icon */}
            <Box sx={{
              width: { xs: 72, sm: 90 },
              height: { xs: 72, sm: 90 },
              borderRadius: '50%',
              bgcolor: alpha(themeObj.palette.warning.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}>
              <LocalShippingIcon sx={{ fontSize: { xs: 36, sm: 44 }, color: 'warning.main' }} />
            </Box>

            {/* Title */}
            <Typography variant="h5" sx={{
              fontWeight: 800,
              color: 'text.primary',
              mb: 1.5,
              fontSize: { xs: '1.1rem', sm: '1.4rem' }
            }}>
              {t('branch.noDeliveryTitle', 'التوصيل غير متاح')}
            </Typography>

            {/* Message */}
            <Typography variant="body1" sx={{
              color: 'text.secondary',
              mb: 1,
              fontSize: { xs: '0.85rem', sm: '1rem' },
              lineHeight: 1.6
            }}>
              {t('branch.noDeliveryMessage', 'عذراً، {{storeName}} لا يوصل إلى مدينتك.', {
                storeName: selectedBranch?.name || selectedBranch?.Name || ''
              })}
            </Typography>

            {/* Store delivery cities info + change city link */}
            {branchCities.length > 0 && (
              <Box sx={{
                mb: 2,
                p: { xs: 1.5, sm: 2 },
                bgcolor: alpha(themeObj.palette.info.main, 0.06),
                borderRadius: 2,
                border: `1px dashed ${alpha(themeObj.palette.info.main, 0.3)}`
              }}>
                <Typography variant="body2" sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  lineHeight: 1.6,
                  mb: 1
                }}>
                  {branchCities.length === 1
                    ? t('branch.ifInCity', 'إذا كنت حالياً في {{cityName}}،', { cityName: branchCities[0].Name || branchCities[0].name })
                    : t('branch.ifInCities', 'إذا كنت حالياً في إحدى هذه المدن: {{cityNames}}،', {
                        cityNames: branchCities.map(c => c.Name || c.name).join(' • ')
                      })
                  }
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => { setCityModalOpen(false); navigate('/profile'); }}
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    fontWeight: 700,
                    textTransform: 'none',
                    textDecoration: 'underline',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
                  }}
                >
                  {t('branch.clickToChangeCity', 'اضغط هنا لتغيير مدينتك')} →
                </Button>
              </Box>
            )}

            {/* Hint */}
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              mb: 3,
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }}>
              {t('branch.noDeliveryHint', 'يرجى اختيار متجر آخر وابدأ التسوق!')}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {/* Two Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Primary: Go to branch selection */}
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  setCityModalOpen(false);
                  navigate('/select-branch', { replace: true });
                }}
                startIcon={<StorefrontIcon />}
                sx={{
                  py: { xs: 1.2, sm: 1.5 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: `0 4px 12px ${alpha(themeObj.palette.primary.main, 0.3)}`
                }}
              >
                {t('branch.chooseAnotherStore', 'اختر متجراً آخر')}
              </Button>

              {/* Secondary: Continue anyway */}
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setCityModalOpen(false)}
                sx={{
                  py: { xs: 1, sm: 1.2 },
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  color: 'text.secondary',
                  borderColor: alpha(themeObj.palette.divider, 0.5),
                  '&:hover': {
                    borderColor: themeObj.palette.divider,
                    bgcolor: alpha(themeObj.palette.action.hover, 0.04)
                  }
                }}
              >
                {t('branch.continueAnyway', 'تصفح على أي حال')}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('Global ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Translation>
          {(t) => (
            <div style={{color:'red',padding:32}}>
              <h2>{t('common.error')}</h2>
              <pre>{String(this.state.error)}</pre>
              <pre>{this.state.errorInfo && this.state.errorInfo.componentStack}</pre>
            </div>
          )}
        </Translation>
      );
    }
    return this.props.children;
  }
}

// ✅ Override CssBaseline to prevent overflow issues
const customTheme = createTheme({
  ...theme,
  components: {
    ...theme.components,
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          overflow: 'visible !important',
          overflowX: 'hidden',
        },
        body: {
          overflow: 'visible !important',
          overflowX: 'hidden',
        },
        '#root': {
          overflow: 'visible !important',
        }
      }
    }
  }
});

// ✅ App Download Redirect Component
function AppRedirect() {
  const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.w3h333.storesye';
  const IOS_URL = 'https://apps.apple.com/ae/app/storesye-%D9%85%D9%86%D8%B5%D8%A9-%D8%B3%D8%AA%D9%88%D8%B1%D8%B2%D9%8A-%D9%84%D9%84%D8%AA%D8%B3-%D9%88%D9%82/id6760149370?l=ar';
  
  const [storeName, setStoreName] = React.useState('App Store');
  const [redirectUrl, setRedirectUrl] = React.useState(ANDROID_URL);

  React.useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isMacOS = /Macintosh/.test(userAgent);
    const isIPadOS = isMacOS && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
    
    let targetUrl = ANDROID_URL;
    let store = 'Google Play Store';
    
    if (isIOS || isIPadOS) {
      targetUrl = IOS_URL;
      store = 'App Store';
    }
    
    setRedirectUrl(targetUrl);
    setStoreName(store);
    
    console.log('📱 Platform detected:', isIOS || isIPadOS ? 'iOS' : 'Android/Other');
    console.log('🔗 Redirecting to:', store);
    
    window.location.replace(targetUrl);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'primary.main',
        color: 'white',
        textAlign: 'center',
        px: 2
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          border: '6px solid rgba(255,255,255,0.3)',
          borderTop: '6px solid white',
          borderRadius: '50%',
          animation: `${spin} 1s linear infinite`,
          mb: 3
        }}
      />
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Redirecting to StoresYe App...
      </Typography>
      <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
        Taking you to {storeName}
      </Typography>
      <Typography 
        component="a" 
        href={redirectUrl}
        sx={{ 
          color: 'white', 
          textDecoration: 'underline',
          fontSize: { xs: '0.85rem', sm: '0.95rem' },
          '&:hover': { opacity: 0.8 }
        }}
      >
        اضغط هنا اذا لم يتم فتح التطبيق تلقائيا
      </Typography>
    </Box>
  );
}

function App() {
  /* const [cartOpen, setCartOpen] = React.useState(false); */
  const { t } = useTranslation();
  
  /* React.useEffect(() => {
    const openCart = () => setCartOpen(true);
    window.addEventListener('openCart', openCart);
    return () => window.removeEventListener('openCart', openCart);
  }, []); */

  return (
    <CartProvider>
      <ThemeProvider theme={customTheme}>
        <CssBaseline />
        <ErrorBoundary>
          <Router>
            <Box
              sx={{
                position: 'relative',
                minHeight: '100vh',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* ✅ BlackHeader - z-index: 1400 */}
              <BlackHeader />
              
              {/* ✅ Main Header - z-index: 1200 */}
              <Header />
              
              {/* ✅ Floating Cart Button - z-index: 1100 */}
              {/* <CartButton onClick={() => setCartOpen(true)} /> */}
              
              {/* ✅ Cart Drawer - ABOVE EVERYTHING */}
              {/* <Drawer 
                anchor="right" 
                open={cartOpen} 
                onClose={() => setCartOpen(false)}
                sx={{
                  zIndex: 1500,
                }}
                PaperProps={{
                  sx: {
                    width: { xs: '100vw', sm: '60vw', md: 420 },
                    zIndex: 1500,
                  }
                }}
                ModalProps={{
                  keepMounted: false,
                  sx: {
                    zIndex: 1500,
                  },
                  BackdropProps: {
                    sx: {
                      zIndex: 1500,
                    }
                  }
                }}
                SlideProps={{
                  sx: {
                    zIndex: 1500,
                  }
                }}
              >
                <Cart onProceed={() => setCartOpen(false)} onClose={() => setCartOpen(false)} />
              </Drawer> */}
              
              {/* ✅ Main Content Area */}
              <Box
                component="main"
                sx={{
                  flex: 1,
                  overflow: 'visible',
                  position: 'relative',
                }}
              >
                <Routes>
                  {/* ✅ App Download Redirect Route */}
                  <Route path="/app" element={<AppRedirect />} />
                  
                  {/* ✅ Branch Deep Link Routes */}
                  <Route path="/branch/:branchId" element={<BranchLinkHandler />} />
                  <Route path="/product/:productId" element={<ProductLinkHandler />} />
                  
                  {/* ✅ Join StoresYe - Public Store Registration */}
                  <Route path="/join" element={<JoinStoresYe />} />
                  
                  {/* ✅ Admin & Branch Selection */}
                  <Route path="/admin/*" element={<AdminAppWrapper />} />
                  <Route path="/select-branch" element={<BranchSelectionPage />} />
                  
                  {/* ✅ Main Routes - WITH CITY MISMATCH GUARD */}
                  <Route path="/" element={
                    <RequireBranch>
                      <CityMismatchGuard>
                        <>
                          <Home />
                          <Categories />
                          <Container 
                            maxWidth="lg" 
                            disableGutters 
                            sx={{ 
                              mt: 0, 
                              px: { xs: 1, sm: 2, md: 3 },
                              overflow: 'visible',
                            }}
                          >
                            <SearchBarEnhanced />
                            <HomeRentalsPreview />
                            <Offers />
                            <GroupedProducts />
                            <CustomProducts />
                            <Products />
                            {/* ✅ NEW: Connected Branches & Reviews */}
                            <BranchConnectedSection />
                          </Container>
                          <BranchFooter />
                        </>
                      </CityMismatchGuard>
                    </RequireBranch>
                  } />

                  <Route path="/prepayment-order/:orderId" element={<RequireBranch><PrePaymentStatusPage /></RequireBranch>} />
                  <Route path="/offers" element={<RequireBranch><OffersPage /></RequireBranch>} />
                  <Route path="/checkout" element={<RequireBranch><CheckoutPage /></RequireBranch>} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/profile" element={<RequireBranch><UserProfile /></RequireBranch>} />
                  <Route path="/order-status/:orderId" element={<RequireBranch><OrderStatusPage /></RequireBranch>} />
                  <Route path="/track-orders" element={<RequireBranch><TrackOrderPage /></RequireBranch>} />
                  <Route path="/order-history" element={<RequireBranch><OrderHistoryPage /></RequireBranch>} />
                  <Route path="/favorites" element={<RequireBranch><Favorites /></RequireBranch>} />
                  <Route path="/locations" element={<RequireBranch><Locations /></RequireBranch>} />
                  <Route path="/locations/add" element={<RequireBranch><AddEditLocation /></RequireBranch>} />
                  <Route path="/locations/edit/:id" element={<RequireBranch><AddEditLocation /></RequireBranch>} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/custom-products" element={<RequireBranch><CustomProducts /></RequireBranch>} />
                  <Route path="/custom-products-preview" element={<RequireBranch><HomeCustomProductsPreview /></RequireBranch>} />
                  <Route path="/policies-terms" element={<PoliciesTerms />} />
                  <Route path="/rentals" element={<RequireBranch><RentalsPage /></RequireBranch>} />
                  <Route path="/rentals/:productId" element={<RequireBranch><RentalDetailPage /></RequireBranch>} />
                  <Route path="/my-rentals" element={<RequireBranch><MyRentalsPage /></RequireBranch>} />
                </Routes>
              </Box>

              <Footer />
            </Box>
          </Router>
        </ErrorBoundary>
      </ThemeProvider>
    </CartProvider>
  );
}

function AdminAppWrapper() {
  return (
    <Box sx={{ mt: { xs: 10, sm: 12 } }}>
      <AdminApp />
    </Box>
  );
}

/* function CartButton({ onClick }) {
  const { cartItems } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const decimals = getCurrencyDecimals(selectedCurrency);
  
  const total = cartItems.reduce((sum, item) => {
    if (item.isCustom && item.CustomTotal) {
      return sum + Number(item.CustomTotal) * Number(item.quantity || 0);
    }
    const itemPrice = getPriceInCurrency(item, selectedCurrency);
    return sum + itemPrice * Number(item.quantity || 0);
  }, 0);
  
  const count = cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  
  if (!count || isNaN(count)) return null;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'fixed',
        left: { xs: 12, sm: 16, md: 24, lg: 32 },
        bottom: { xs: 70, sm: 90, md: 120 },
        zIndex: 1100,
        cursor: 'pointer',
        animation: `${slideInRight} 0.5s ease-out, ${float} 3s ease-in-out infinite`,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'scale(1.05)',
          '& .cart-content': {
            maxWidth: { xs: 200, sm: 280, md: 320, lg: 380 },
            opacity: 1
          },
          '& .cart-icon-wrapper': {
            transform: 'rotate(10deg) scale(1.1)',
          },
          '& .total-badge': {
            transform: 'scale(1.1)',
            animation: `${pulse} 0.6s ease-in-out infinite`
          }
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5, md: 2 },
          bgcolor: 'primary.main',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(
              135deg,
              ${alpha(theme.palette.common.white, 0.1)} 0%,
              ${alpha(theme.palette.common.white, 0)} 100%
            )`,
            pointerEvents: 'none'
          }
        }}
      >
        <Box
          className="cart-icon-wrapper"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: 56, sm: 64, md: 72, lg: 80 },
            height: { xs: 56, sm: 64, md: 72, lg: 80 },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            flexShrink: 0
          }}
        >
          <Badge
            badgeContent={count}
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: 'error.main',
                color: 'white',
                fontWeight: 900,
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.85rem' },
                minWidth: { xs: 20, sm: 22, md: 26 },
                height: { xs: 20, sm: 22, md: 26 },
                borderRadius: '50%',
                border: '3px solid',
                borderColor: 'primary.main',
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.5)}`,
                animation: count > 0 ? `${bounce} 1s ease` : 'none'
              }
            }}
          >
            <ShoppingCartIcon
              sx={{
                fontSize: { xs: 28, sm: 32, md: 36, lg: 40 },
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }}
            />
          </Badge>

          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              border: '3px solid',
              borderColor: alpha(theme.palette.common.white, 0.3),
              borderRadius: '50%',
              animation: `${pulse} 2s ease-in-out infinite`,
              pointerEvents: 'none'
            }}
          />
        </Box>

        <Box
          className="cart-content"
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            gap: 0.5,
            pr: { sm: 2, md: 3 },
            py: { sm: 1.5, md: 2 },
            maxWidth: { sm: 200, md: 240, lg: 280 },
            opacity: { xs: 0, sm: 1 },
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            whiteSpace: 'nowrap'
          }}
        >
          <Typography
            sx={{
              color: 'white',
              fontWeight: 800,
              fontSize: { sm: '1rem', md: '1.1rem', lg: '1.25rem' },
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {t('navigation.cart', 'Cart')}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${t('orderStages.common.items', 'Items')} : ${count}`}
              size="small"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: { sm: '0.7rem', md: '0.75rem' },
                height: { sm: 22, md: 24 },
                '& .MuiChip-label': { px: { sm: 1, md: 1.5 } }
              }}
            />
          </Box>

          <Box
            className="total-badge"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: alpha(theme.palette.common.white, 0.15),
              borderRadius: 3,
              px: { sm: 1.5, md: 2 },
              py: { sm: 0.75, md: 1 },
              mt: 0.5,
              transition: 'transform 0.3s ease'
            }}
          >
            <LocalOfferIcon sx={{ fontSize: { sm: 16, md: 18 }, color: 'white' }} />
            <Box>
              <Typography
                sx={{
                  color: alpha(theme.palette.common.white, 0.8),
                  fontSize: { sm: '0.6rem', md: '0.65rem' },
                  fontWeight: 600,
                  lineHeight: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {t('cart.total', 'Total')}
              </Typography>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: { sm: '1rem', md: '1.1rem', lg: '1.25rem' },
                  fontWeight: 900,
                  lineHeight: 1.2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {total.toFixed(decimals)} {currencySymbol}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(
              90deg,
              transparent,
              ${alpha(theme.palette.common.white, 0.15)},
              transparent
            )`,
            backgroundSize: '200% 100%',
            animation: `${shine} 3s infinite`,
            pointerEvents: 'none'
          }}
        />
      </Box>

      {isMobile && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -6,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'error.main',
            color: 'white',
            px: 1.5,
            py: 0.3,
            borderRadius: 3,
            fontSize: '0.7rem',
            fontWeight: 800,
            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`,
            whiteSpace: 'nowrap'
          }}
        >
          {total.toFixed(decimals)} {currencySymbol}
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          bottom: -24,
          left: 8,
          color: 'white',
          fontSize: '0.7rem',
          fontWeight: 600,
          bgcolor: alpha(theme.palette.common.black, 0.6),
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          display: { xs: 'none', md: 'block' },
          animation: `${float} 2s ease-in-out infinite`,
          pointerEvents: 'none'
        }}
      >
        {t('cart.clickToOpen', 'Click to open')} 👆
      </Box>
    </Box>
  );
} */

export default App;
